import { useEffect, useMemo, useRef, useState } from "react"

import Spreadsheet from "react-spreadsheet"
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { ChartSpec } from "../renderers/chart-spec"
import type { SandboxFrameHandle } from "../renderers/sandbox-frame"
import type { ArtifactEntry } from "../stores/artifact-store"

import { fetchFileContentApi } from "@/api/api"
import { documentContentUrl } from "@/api/client/documents"
import { ChartContainer } from "@/components/ui/chart"
import { CodeBlock } from "@/features/chat/components/code-block"
import { SettingsService } from "@/services/settings-service"

import { parseChartSpec } from "../renderers/chart-spec"
import { buildReactSrcDoc } from "../renderers/react-srcdoc"
import { resolveArtifactImages } from "../renderers/resolve-artifact-images"
import { SandboxFrame } from "../renderers/sandbox-frame"
import { sanitizeInlineSvg } from "../renderers/sanitize-inline-svg"
import { parseCsv } from "../renderers/sheet-data"

const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
]

// The chart fills are pale (~1.2-1.4:1 on the white chart ground), so strokes
// and shape edges get a foreground outline to reach the 3:1 non-text contrast
// of WCAG 1.4.11 while each series keeps its own colour for identity.
const CHART_OUTLINE = "hsl(var(--foreground))"
const LINE_WIDTH = 2

type ArtifactPreviewProps = {
  entry: ArtifactEntry
  content: string
  printRef?: React.Ref<SandboxFrameHandle>
  onRuntimeError: (message: string) => void
}

export function ArtifactPreview({
  entry,
  content,
  printRef,
  onRuntimeError,
}: ArtifactPreviewProps) {
  switch (entry.type) {
    case "html":
      return (
        <SandboxWithImages
          content={content}
          entry={entry}
          printRef={printRef}
          onRuntimeError={onRuntimeError}
        />
      )
    case "chart":
      return <ChartPreview content={content} />
    case "sheet":
      return <SheetPreview content={content} />
    case "svg":
      return <SvgPreview content={content} />
    case "mermaid":
      // CodeBlock handles mermaid language via MermaidBlock internally
      return <CodeBlock className="language-mermaid">{content}</CodeBlock>
    case "slides":
      return (
        <SandboxWithImages
          content={content}
          entry={entry}
          printRef={printRef}
          onRuntimeError={onRuntimeError}
        />
      )
    case "react":
      return (
        <SandboxWithImages
          allowEval
          content={content}
          entry={entry}
          transform={buildReactSrcDoc}
          onRuntimeError={onRuntimeError}
        />
      )
    default:
      // markdown opens in canvas, not here
      return <CodeBlock className="language-html">{content}</CodeBlock>
  }
}

type SandboxWithImagesProps = {
  entry: ArtifactEntry
  content: string
  allowEval?: boolean
  /** Applied AFTER image resolution (react → buildReactSrcDoc). */
  transform?: (raw: string) => string
  printRef?: React.Ref<SandboxFrameHandle>
  onRuntimeError: (message: string) => void
}

/**
 * Resolves session-image references in the artifact content to inline data:
 * URIs BEFORE building the sandbox srcdoc, so the sandbox iframe (CSP img-src
 * data:) can display generated images. Fetching is async, so a small loading
 * placeholder is shown until the content is resolved. When the artifact has no
 * session context (no sessionId or no filenames) nothing needs fetching and the
 * frame renders immediately. Fetch failures fall back to the unresolved content.
 */
function SandboxWithImages({
  entry,
  content,
  allowEval,
  transform,
  printRef,
  onRuntimeError,
}: SandboxWithImagesProps) {
  const [resolved, setResolved] = useState<string | null>(null)
  const imageSource = entry.imageSource
  const documentId = imageSource?.kind === "document" ? imageSource.documentId : ""
  // Session source comes from imageSource; fall back to entry.sessionId for
  // entries created before imageSource existed (and in older tests).
  const sessionFromEntry =
    imageSource?.kind === "session" ? imageSource.sessionId : (entry.sessionId ?? "")
  const sessionId = documentId ? "" : sessionFromEntry
  const filenames = entry.sessionFilenames ?? []
  // Stable key so the effect re-runs only when the referenced files change,
  // not on every render (a fresh [] from ?? would otherwise retrigger it).
  const filenamesKey = filenames.join(",")

  // Cache in-flight/resolved byte fetches by URL so streaming re-resolves (the
  // effect re-runs on every `content` change) don't re-hit the content endpoint
  // for the same file on every chunk. Caching the promise also dedupes concurrent
  // fetches; a rejected fetch is evicted so a later resolve can retry.
  const bytesCacheRef = useRef<Map<string, Promise<ArrayBuffer>>>(new Map())

  useEffect(() => {
    let cancelled = false
    // No image source or no referenced images → render immediately.
    if ((!sessionId && !documentId) || filenames.length === 0) {
      setResolved(transform ? transform(content) : content)
      return
    }
    void resolveArtifactImages(content, {
      sessionId,
      filenames,
      // Saved artifacts serve images from document-scoped storage; live ones from
      // the session. The refs in `content` are identical — only the URL differs.
      buildUrl: documentId ? (f) => documentContentUrl(documentId, f) : undefined,
      fetchBytes: (url) => {
        const cache = bytesCacheRef.current
        const cached = cache.get(url)
        if (cached) return cached
        const pending = fetchFileContentApi(url, SettingsService.getInstance().token).catch(
          (err) => {
            cache.delete(url)
            throw err
          }
        )
        cache.set(url, pending)
        return pending
      },
    })
      .then((withImages) => {
        if (!cancelled) setResolved(transform ? transform(withImages) : withImages)
      })
      .catch(() => {
        if (!cancelled) setResolved(transform ? transform(content) : content)
      })
    return () => {
      cancelled = true
    }
    // `filenames` is intentionally excluded (its identity changes every render);
    // `filenamesKey` is its stable content key.
  }, [content, sessionId, documentId, filenamesKey, transform])

  if (resolved === null) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center p-6 text-sm">
        …
      </div>
    )
  }

  return (
    <SandboxFrame
      ref={printRef}
      allowEval={allowEval}
      html={resolved}
      title={entry.title}
      onRuntimeError={onRuntimeError}
    />
  )
}

function ChartPreview({ content }: { content: string }) {
  const result = useMemo(() => parseChartSpec(content), [content])

  if (!result.ok) {
    // The parent panel derives the error via its own useMemo and forces the
    // code tab via effectiveTab — no render-time setState needed here.
    // Render a code fallback so ChartPreview is self-contained if ever used
    // outside ArtifactPanel.
    return <CodeBlock className="language-json">{content}</CodeBlock>
  }

  const { spec } = result

  // Build a minimal ChartConfig from yKeys so ChartStyle can generate CSS vars.
  // ChartContainer accepts config={} (empty) — ChartStyle returns null when no
  // color entries exist, which is fine. We build config for completeness but
  // it is not strictly required for the standard Recharts components we use.
  const chartConfig = Object.fromEntries(
    spec.yKeys.map((key, i) => [key, { label: key, color: CHART_COLORS[i % CHART_COLORS.length] }])
  )

  const commonAxes = (
    <>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey={spec.xKey} tick={{ fontSize: 11 }} />
      <YAxis tick={{ fontSize: 11 }} />
      <Tooltip />
      <Legend />
    </>
  )

  return (
    <div className="p-4">
      {spec.title ? <h3 className="mb-2 text-sm font-semibold">{spec.title}</h3> : null}
      {/* Width-driven, not aspect-driven: ChartContainer's `aspect-video` would
          force width = height × 16/9 (~640px at 360px tall) and overflow a
          narrow chat column, clipping the chart (QA 2026-09-13, 390px mobile).
          A full-width wrapper with an explicit height lets ResponsiveContainer
          size to the column instead. */}
      <div className="h-[280px] w-full sm:h-[360px]">
        <ChartContainer className="aspect-auto h-full w-full" config={chartConfig}>
          <ChartBody commonAxes={commonAxes} spec={spec} />
        </ChartContainer>
      </div>
    </div>
  )
}

type ChartBodyProps = {
  spec: ChartSpec
  commonAxes: React.ReactNode
}

function ChartBody({ spec, commonAxes }: ChartBodyProps) {
  if (spec.chartType === "bar") {
    return (
      <BarChart data={spec.data}>
        {commonAxes}
        {spec.yKeys.map((key, i) => (
          <Bar
            key={key}
            dataKey={key}
            fill={CHART_COLORS[i % CHART_COLORS.length]}
            radius={[4, 4, 0, 0]}
            stroke={CHART_OUTLINE}
            strokeWidth={1}
          />
        ))}
      </BarChart>
    )
  }
  if (spec.chartType === "line") {
    return (
      <LineChart data={spec.data}>
        {commonAxes}
        {/* Underlays first so they render behind the coloured strokes. They are
            excluded from legend and tooltip, so both keep one entry per series. */}
        {spec.yKeys.map((key) => (
          <Line
            key={`${key}-underlay`}
            activeDot={false}
            dataKey={key}
            dot={false}
            legendType="none"
            stroke={CHART_OUTLINE}
            strokeWidth={LINE_WIDTH + 2}
            tooltipType="none"
            type="monotone"
          />
        ))}
        {spec.yKeys.map((key, i) => (
          <Line
            key={key}
            dataKey={key}
            dot={{ stroke: CHART_OUTLINE, strokeWidth: 1 }}
            stroke={CHART_COLORS[i % CHART_COLORS.length]}
            strokeWidth={LINE_WIDTH}
            type="monotone"
          />
        ))}
      </LineChart>
    )
  }
  if (spec.chartType === "area") {
    return (
      <AreaChart data={spec.data}>
        {commonAxes}
        {spec.yKeys.map((key) => (
          <Area
            key={`${key}-underlay`}
            activeDot={false}
            dataKey={key}
            dot={false}
            fill="none"
            legendType="none"
            stroke={CHART_OUTLINE}
            strokeWidth={LINE_WIDTH + 2}
            tooltipType="none"
            type="monotone"
          />
        ))}
        {spec.yKeys.map((key, i) => (
          <Area
            key={key}
            dataKey={key}
            fill={CHART_COLORS[i % CHART_COLORS.length]}
            fillOpacity={0.3}
            stroke={CHART_COLORS[i % CHART_COLORS.length]}
            strokeWidth={LINE_WIDTH}
            type="monotone"
          />
        ))}
      </AreaChart>
    )
  }
  // pie
  return (
    <PieChart>
      <Tooltip />
      <Legend />
      <Pie label data={spec.data} dataKey={spec.yKeys[0]} nameKey={spec.xKey}>
        {spec.data.map((_, i) => (
          <Cell
            key={i}
            fill={CHART_COLORS[i % CHART_COLORS.length]}
            stroke={CHART_OUTLINE}
            strokeWidth={1}
          />
        ))}
      </Pie>
    </PieChart>
  )
}

function SheetPreview({ content }: { content: string }) {
  const { headers, rows } = useMemo(() => parseCsv(content), [content])
  // react-spreadsheet 0.10.1 has columnLabels?: string[] — use it directly.
  const data = useMemo(
    () => rows.map((row) => row.map((value) => ({ value, readOnly: true }))),
    [rows]
  )
  return (
    <div className="overflow-auto p-2">
      <Spreadsheet className="!w-full" columnLabels={headers} data={data} />
    </div>
  )
}

function SvgPreview({ content }: { content: string }) {
  const sanitized = useMemo(() => sanitizeInlineSvg(content), [content])

  return <div className="flex justify-center p-4" dangerouslySetInnerHTML={{ __html: sanitized }} />
}
